import { Alert, Button, Col, Row, Tab, Tabs, Form, Nav } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowUpRightFromSquare,
    faCheckCircle,
    faSpinner,
    faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { callWrite, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/appsync/write";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PATIENT_VISIT_BY_CSN } from "../../../amplify/appsync/query";
import { readBinaryFile, getFileUrl } from "../../../amplify/s3/storage";
import useSetPagePatientVisit from "../../common/GlobalOptionsContext";

interface DischargeInstructionSurvey {
    completeness: number;
    correctness: number;
    readability: number;
    replaceOriginal: number;
    improveUnderstanding: number;
    formatAcceptability: number;
    followInstructions: number;
    comfortSupervised: number;
    comfortUnsupervised: number;
    mostUsefulAspects: string;
    improvements: string;
    practicalUse: string;
}

interface SurveyStatus {
    isSubmitted: boolean;
    isUpdate?: boolean;
    submittedAt?: number;
}

interface SurveyStatuses {
    original: SurveyStatus;
    video: SurveyStatus;
}

interface BaseSurveyQuestions {
    completeness: number;
    correctness: number;
    readability: number;
}

interface OriginalSurvey extends BaseSurveyQuestions {
    type: 'original';
}

interface VideoSurvey extends DischargeInstructionSurvey {
    type: 'video';
    supplementOriginal: number;
}

type SurveyType = OriginalSurvey | VideoSurvey;

interface SurveyFormProps<T extends SurveyType> {
    survey: T;
    setSurvey: (survey: T) => void;
    surveyType: T['type'];
    onSubmit?: (survey: T) => void;
    isSubmitted?: boolean;
    isUpdate?: boolean;
    submittedAt?: number;
}

interface ExplanationDashboardProps {
    csn: string;
    tenant?: string;
}

const SurveyForm = <T extends SurveyType>({ 
    survey, 
    setSurvey,
    surveyType,
    onSubmit,
    isSubmitted,
    isUpdate,
    submittedAt
}: SurveyFormProps<T>) => {
    const [isSaving, setIsSaving] = useState(false);
    const [lastSubmitted, setLastSubmitted] = useState<T | null>(null);

    const handleSubmit = () => {
        setIsSaving(true);
        setLastSubmitted({...survey});
        
        if (onSubmit) {
            onSubmit(survey);
        }
        
        setTimeout(() => {
            setIsSaving(false);
        }, 1000);
    };

    const handleInputChange = (field: string, value: string) => {
        // Check if the field is one of the text fields
        const textFields = ['mostUsefulAspects', 'improvements', 'practicalUse'];
        if (textFields.includes(field)) {
            // Handle text input
            setSurvey({ ...survey, [field]: value });
        } else {
            // Handle numeric input
            const numValue = parseInt(value);
            setSurvey({ ...survey, [field]: numValue });
        }
    };

    const renderScoreRadioGroup = (field: string, label: string) => (
        <Form.Group className="mb-3">
            <Form.Label dangerouslySetInnerHTML={{ __html: label }}></Form.Label>
            <div className="d-flex justify-content-between align-items-center">
                {[1, 2, 3, 4, 5].map((score) => (
                    <div key={score} className="text-center" style={{ flex: 1 }}>
                        <Form.Check
                            type="radio"
                            id={`${field}-${score}`}
                            name={field}
                            value={score}
                            checked={survey[field as keyof T] === score}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(field, e.target.value)}
                        />
                        <Form.Label 
                            htmlFor={`${field}-${score}`}
                            className="small"
                        >
                            {score}
                        </Form.Label>
                    </div>
                ))}
            </div>
        </Form.Group>
    );

    const hasChanges = lastSubmitted === null || 
        JSON.stringify(lastSubmitted) !== JSON.stringify(survey);

    return (
        <Form className="survey-form">
            {isSubmitted && submittedAt && (
                <Alert variant="success" className="mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} /> 
                    {isUpdate ? "Updated" : "Submitted"} on {new Date(submittedAt).toLocaleString()}
                </Alert>
            )}
            
            <h6>Assessment</h6>
            {/* Base questions for all surveys */}
            {renderScoreRadioGroup('completeness', 'Completeness (1-5)')}
            {renderScoreRadioGroup('correctness', 'Correctness (1-5)')}
            {renderScoreRadioGroup('patient accessibility', 'Patient accessibility (1-5)')}

            {/* Full question set for video survey */}
            {surveyType === 'video' && (
                <>
                    <h6>Video-Specific Questions</h6>
                    <p>Please rate the following questions on a scale of 1-5, where 1=very unlikely, 2=unlikely, 3=maybe, 4=likely, 5=very likely.</p>
                    {renderScoreRadioGroup('improveUnderstanding', 'Do you think the generated video can improve this patient\'s understanding?')}
                    {renderScoreRadioGroup('formatAcceptability', 'Do you think the video format will be more acceptable for patients?')}
                    {renderScoreRadioGroup('followInstructions', 'Do you think this video will better help patients follow the discharge instruction?')}
                    {renderScoreRadioGroup('comfortSupervised', 'Would you feel comfortable walking patients through this video under your supervision?')}
                    {renderScoreRadioGroup('comfortUnsupervised', 'Would you feel comfortable letting patients watch this video by themselves?')}
                    {renderScoreRadioGroup('supplementOriginal', 
                        'Would you consider <u>supplementing</u> the original discharge instruction with this generated instruction?')}
                    {renderScoreRadioGroup('replaceOriginal', 
                        'Would you consider <u>replacing</u> the original discharge instruction with this generated instruction?')}
                    
                    <h6>Open Questions</h6>
                    <Form.Group className="mb-2">
                        <Form.Label>What aspects were most useful or valuable?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={(survey as VideoSurvey).mostUsefulAspects}
                            onChange={(e) => handleInputChange('mostUsefulAspects', e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>How would you improve it?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={(survey as VideoSurvey).improvements}
                            onChange={(e) => handleInputChange('improvements', e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>How would you use it in practice?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={(survey as VideoSurvey).practicalUse}
                            onChange={(e) => handleInputChange('practicalUse', e.target.value)}
                        />
                    </Form.Group>
                </>
            )}

            <div className="d-grid gap-2 mt-4">
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={isSaving || !hasChanges}
                    className="survey-submit-btn"
                >
                    {isSaving ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                        </>
                    ) : hasChanges ? (
                        'Submit Survey'
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faCheckCircle} /> Submitted
                        </>
                    )}
                </Button>
            </div>
        </Form>
    );
};

const RawDataTable = ({ data, columns }: { data: any, columns?: string[] }): JSX.Element => {
    if (!data) {
        return <Alert variant="info">No data available</Alert>;
    }
    
    if (columns && Array.isArray(data)) {
        if (data.length === 0) {
            return <Alert variant="info">No data available</Alert>;
        }

        return (
            <Table size="sm" bordered hover responsive>
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th key={index}>{col.replace(/_/g, ' ')}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, rowIndex) => (
                        <tr key={rowIndex}>
                            {columns.map((col, colIndex) => (
                                <td key={`${rowIndex}-${colIndex}`}>
                                    {item[col]?.toString() || ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    const renderValue = (value: any): JSX.Element => {
        if (Array.isArray(value)) {
            return (
                <ul className="mb-0 ps-3">
                    {value.map((item, index) => (
                        <li key={index}>{typeof item === 'object' ? renderValue(item) : String(item)}</li>
                    ))}
                </ul>
            );
        } else if (typeof value === 'object' && value !== null) {
            return (
                <Table size="sm" bordered className="mb-0">
                    <tbody>
                        {Object.entries(value).map(([k, v]) => (
                            <tr key={k}>
                                <td className="fw-bold" style={{width: '40%'}}>{k}</td>
                                <td>{typeof v === 'object' ? renderValue(v) : String(v)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            );
        }
        return <span>{String(value)}</span>;
    };

    return (
        <div className="visit-data-wrapper">
            {renderValue(data)}
        </div>
    );
};

export const ExplanationDashboard = ({
    csn,
    tenant = 'stanford'
}: ExplanationDashboardProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [visitData, setVisitData] = useState<any>(null);
    const [patientDocuments, setPatientDocuments] = useState("");
    const [originalDischargeInstruction, setOriginalDischargeInstruction] = useState("");
    const [generatedDischargeSummary, setGeneratedDischargeSummary] = useState("");
    const [edNote, setEdNote] = useState("");
    const [nextVisitEdNote, setNextVisitEdNote] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    
    // Survey states
    const [originalSurvey, setOriginalSurvey] = useState<OriginalSurvey>({
        type: 'original',
        completeness: 0,
        correctness: 0,
        readability: 0
    });
    
    const [videoSurvey, setVideoSurvey] = useState<VideoSurvey>({
        type: 'video',
        completeness: 0,
        correctness: 0,
        readability: 0,
        replaceOriginal: 0,
        improveUnderstanding: 0,
        formatAcceptability: 0,
        followInstructions: 0,
        comfortSupervised: 0,
        comfortUnsupervised: 0,
        supplementOriginal: 0,
        mostUsefulAspects: '',
        improvements: '',
        practicalUse: ''
    });
    
    const [surveyStatuses, setSurveyStatuses] = useState<SurveyStatuses>({
        original: { isSubmitted: false },
        video: { isSubmitted: false }
    });

    const patientDataRef = useRef<HTMLDivElement>(null);
    const edNotesRef = useRef<HTMLDivElement>(null);
    const dischargeComparisonRef = useRef<HTMLDivElement>(null);
    const videoReviewRef = useRef<HTMLDivElement>(null);

    const [activeSection, setActiveSection] = useState('patient-data');
    const [activeDataTab, setActiveDataTab] = useState('visit_data');

    // Add patientVisit state directly in the component
    const [patientVisit, setPatientVisit] = useState<PatientVisit>();
    useSetPagePatientVisit(patientVisit);

    // Add function to check if all surveys are complete
    const areAllSurveysComplete = () => {
        return surveyStatuses.original.isSubmitted && 
               surveyStatuses.video.isSubmitted;
    };

    // Add a completion status banner
    const CompletionBanner = () => (
        <Alert variant={areAllSurveysComplete() ? "success" : "warning"} className="mb-3">
            <h6>Survey Completion Status:</h6>
            <ul className="mb-0">
                {!surveyStatuses.original.isSubmitted && 
                    <li>Original Discharge Instructions Survey pending</li>}
                {!surveyStatuses.video.isSubmitted && 
                    <li>Video Review Survey pending</li>}
                {areAllSurveysComplete() && 
                    <span>✓ All surveys completed!</span>}
            </ul>
        </Alert>
    );

    // Add scroll to section function
    const scrollToSection = (sectionRef: React.RefObject<HTMLDivElement>, section: string) => {
        if (sectionRef.current) {
            const headerOffset = 74; // Adjust based on your header height
            const elementPosition = sectionRef.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            
            setActiveSection(section);
        }
    };

    // Add scroll position detection
    useEffect(() => {
        const handleScroll = () => {
            const headerOffset = 64;
            const scrollPosition = window.scrollY + headerOffset;

            if (patientDataRef.current && 
                edNotesRef.current &&
                dischargeComparisonRef.current && 
                videoReviewRef.current) {
                
                // Get positions of all sections
                const patientDataPos = patientDataRef.current.offsetTop;
                const edNotesPos = edNotesRef.current.offsetTop;
                const dischargePos = dischargeComparisonRef.current.offsetTop;
                const videoPos = videoReviewRef.current.offsetTop;

                // Add a small offset to make the detection more accurate
                const bufferZone = 50;

                // Update active section based on current scroll position
                if (scrollPosition < edNotesPos - bufferZone) {
                    setActiveSection('patient-data');
                } else if (scrollPosition >= edNotesPos - bufferZone && scrollPosition < dischargePos - bufferZone) {
                    setActiveSection('ed-notes');
                } else if (scrollPosition >= dischargePos - bufferZone && scrollPosition < videoPos - bufferZone) {
                    setActiveSection('discharge-comparison');
                } else {
                    setActiveSection('video-review');
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Add function to fetch patient visit data
    const fetchPatientVisit = async () => {
        try {
            const patients = await callQuery({
                operation: PATIENT_VISIT_BY_CSN,
                csn: csn,
            });
            
            if (Array.isArray(patients) && patients.length > 0) {
                setPatientVisit(patients[0]);
            } else {
                setPatientVisit(undefined);
            }
        } catch (error) {
            console.error("Error fetching patient visit:", error);
            setPatientVisit(undefined);
        }
    };

    // Function to fetch dashboard data
    const fetchDashboardData = async () => {
        setIsLoading(true);
        const decoder = new TextDecoder();
        
        try {
            // Fetch UUID mapping first
            const summaryData = await readBinaryFile(
                'explanation_dashboard/2025-04-05/summary.txt',
                tenant,
                'ed-llm'
            );
            
            const summaryText = decoder.decode(summaryData);
            const summaryLines = summaryText
                .split('\n')
                .filter(line => line.trim())
                .map(line => line.split(','));
            
            const uuidMapping = Object.fromEntries(
                summaryLines.map(line => [line[1], line[0]])
            );
            
            const uuid = uuidMapping[csn];
            if (!uuid) {
                setErrorMessage("CSN not found in summary file.");
                setIsLoading(false);
                return;
            }
            
            // Fetch the actual data
            const dashboardPath = `explanation_dashboard/2025-04-05/${uuid}.json`;
            const data = await readBinaryFile(
                dashboardPath,
                tenant,
                'ed-llm'
            );
            
            const jsonData = JSON.parse(decoder.decode(data));
            
            // Set all required state variables
            setVisitData(jsonData.visit_data || {});
            setPatientDocuments(jsonData.patient_documents || "");
            setOriginalDischargeInstruction(jsonData.original_discharge_instruction || "");
            setGeneratedDischargeSummary(jsonData.generated_discharge_summary || "");
            setEdNote(jsonData.ED_Note || "");
            setNextVisitEdNote(jsonData.Next_Visit_ED_Note || "");
            
            let mrn = '';
            if (jsonData.visit_data) {
                // Try different possible field names
                mrn = jsonData.visit_data.MRN || 
                      jsonData.visit_data.mrn || 
                      '';
            }
            
            if (mrn) {
                await fetchVideo(mrn, csn);
            } else {
                console.error("No MRN found in visit data");
            }
            
            // Fetch survey data
            await fetchSurveyData();
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to fetch video
    const fetchVideo = async (mrn: string, visitCsn: string) => {
        try {
            const videoKey = `explanation_dashboard/2025-04-05/videos/${mrn}_${visitCsn}.mp4`;
            const url = await getFileUrl(videoKey, tenant, 'ed-llm');
            setVideoUrl(url);
        } catch (error) {
            console.error("Error fetching video:", error);
            // Don't show error for video, just log it
        }
    };
    
    // Function to fetch survey data
    const fetchSurveyData = async () => {
        try {
            console.log("Fetching survey data for CSN:", csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn,
            }) as LLMVisitFeedback[];
            
            console.log("Survey data result:", feedbackItems);
            
            if (feedbackItems && feedbackItems.length > 0) {
                // Get all original survey feedback items
                const originalSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'survey' && 
                        (item.feedback_target === 'original_discharge' || 
                         item.feedback_target === 'original_survey')
                );
                
                // Get all video survey feedback items
                const videoSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'survey' && 
                        (item.feedback_target === 'video' || 
                         item.feedback_target === 'video_survey')
                );
                
                // Process original survey feedback - find the one with the most recent timestamp
                if (originalSurveyFeedbackItems.length > 0) {
                    const originalSurveyFeedback = originalSurveyFeedbackItems
                        .map(item => {
                            const creationTime = getTimeInMs(item.creation_time);
                            const updateTime = getTimeInMs(item.update_time);
                            
                            // Use the newer timestamp
                            const effectiveTime = Math.max(creationTime, updateTime);
                            
                            return {
                                ...item,
                                effectiveTime,
                                isUpdate: updateTime > creationTime
                            };
                        })
                        .sort((a, b) => b.effectiveTime - a.effectiveTime)[0]; // Sort descending and take the first
                    
                    try {
                        const originalSurveyData = JSON.parse(originalSurveyFeedback.note);
                        setOriginalSurvey(originalSurveyData);
                        setSurveyStatuses(prev => ({
                            ...prev,
                            original: { 
                                isSubmitted: true,
                                isUpdate: originalSurveyFeedback.isUpdate,
                                submittedAt: originalSurveyFeedback.effectiveTime
                            }
                        }));
                    } catch (e) {
                        console.error("Error parsing original survey data:", e);
                    }
                }
                
                // Process video survey feedback - find the one with the most recent timestamp
                if (videoSurveyFeedbackItems.length > 0) {
                    const videoSurveyFeedback = videoSurveyFeedbackItems
                        .map(item => {
                            const creationTime = getTimeInMs(item.creation_time);
                            const updateTime = getTimeInMs(item.update_time);
                            
                            // Use the newer timestamp
                            const effectiveTime = Math.max(creationTime, updateTime);
                            
                            return {
                                ...item,
                                effectiveTime,
                                isUpdate: updateTime > creationTime
                            };
                        })
                        .sort((a, b) => b.effectiveTime - a.effectiveTime)[0]; // Sort descending and take the first
                    
                    try {
                        const videoSurveyData = JSON.parse(videoSurveyFeedback.note);
                        setVideoSurvey(videoSurveyData);
                        setSurveyStatuses(prev => ({
                            ...prev,
                            video: { 
                                isSubmitted: true, 
                                isUpdate: videoSurveyFeedback.isUpdate, 
                                submittedAt: videoSurveyFeedback.effectiveTime
                            }
                        }));
                    } catch (e) {
                        console.error("Error parsing video survey data:", e);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching survey data:", error);
        }
    };
    
    // Function to submit original survey
    const handleOriginalSurveySubmit = async (survey: OriginalSurvey): Promise<boolean> => {
        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: csn,
                mrn: '',
                feedback_target: 'original_discharge',
                feedback_type: 'survey',
                note: JSON.stringify(survey),
            });
            
            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Server returned an empty response.");
                return false;
            }
            
            // Refresh the survey data after submission
            await fetchSurveyData();
            
            return true;
        } catch (error) {
            console.error("Error submitting original survey:", error);
            
            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }
            
            return false;
        }
    };
    
    // Function to submit video survey
    const handleVideoSurveySubmit = async (survey: VideoSurvey): Promise<boolean> => {
        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: csn,
                mrn: '',
                feedback_target: 'video',
                feedback_type: 'survey',
                note: JSON.stringify(survey),
            });
            
            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Server returned an empty response.");
                return false;
            }
            
            // Refresh the survey data after submission
            await fetchSurveyData();
            
            return true;
        } catch (error) {
            console.error("Error submitting video survey:", error);
            
            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }
            
            return false;
        }
    };
    
    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (csn) {
            fetchDashboardData();
            fetchPatientVisit();
        }
    }, [csn, tenant]);

    if (isLoading) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading dashboard data...
                </Col>
            </Row>
        );
    }
    
    if (errorMessage) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <Alert variant="danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
                    </Alert>
                </Col>
            </Row>
        );
    }

    return (
        <div style={{ margin: '0 60px' }}>
            <CompletionBanner />

            {/* Left Side Navigation */}
            <div className="section-nav" style={{ left: '20px' }}>
                <div className="section-nav-line"></div>
                <div 
                    className={`section-nav-item ${activeSection === 'patient-data' ? 'active' : ''}`}
                    onClick={() => scrollToSection(patientDataRef, 'patient-data')}
                >
                    <div className="section-nav-bubble"></div>
                    <div className="section-nav-text">Patient Data</div>
                </div>
                <div 
                    className={`section-nav-item ${activeSection === 'ed-notes' ? 'active' : ''}`}
                    onClick={() => scrollToSection(edNotesRef, 'ed-notes')}
                >
                    <div className="section-nav-bubble"></div>
                    <div className="section-nav-text">ED Notes</div>
                </div>
                <div 
                    className={`section-nav-item ${activeSection === 'discharge-comparison' ? 'active' : ''}`}
                    onClick={() => scrollToSection(dischargeComparisonRef, 'discharge-comparison')}
                >
                    <div className="section-nav-bubble"></div>
                    <div className="section-nav-text">Discharge Instructions</div>
                </div>
                <div 
                    className={`section-nav-item ${activeSection === 'video-review' ? 'active' : ''}`}
                    onClick={() => scrollToSection(videoReviewRef, 'video-review')}
                >
                    <div className="section-nav-bubble"></div>
                    <div className="section-nav-text">Video Review</div>
                </div>
            </div>

            {/* Content with additional left padding for the navigation */}
            <div style={{ paddingLeft: '40px' }}>
                <div ref={patientDataRef}>
                    <Row className="my-4">
                        <Col>
                            <div className="d-flex align-items-center">
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                                <div className="px-3">
                                    <h4 className="mb-0 text-muted">Patient Data</h4>
                                </div>
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                            </div>
                        </Col>
                    </Row>

                    <Row style={{marginBottom: '12px'}}>
                        <Col md={6}>
                            <Row>
                                <Col>
                                    <h5>Raw Data</h5>
                                </Col>
                                <Col className="text-right float-end">
                                    {
                                        patientVisit && (
                                            <Button variant="outline-secondary" size="sm" onClick={() => {
                                                window.open(`/dashboard/${patientVisit.internal_csn}`, "_blank");
                                            }}><FontAwesomeIcon icon={faArrowUpRightFromSquare} /> See on Capacity</Button>
                                        )
                                    }
                                </Col>
                            </Row>
                            <div className="visit-data-container" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <Tabs
                                    activeKey={activeDataTab}
                                    onSelect={(k: any) => setActiveDataTab(k)}
                                    className="mb-3"
                                >
                                    <Tab eventKey="visit_data" title="Visit Info">
                                        <strong>CSN:</strong> {csn}
                                        <RawDataTable data={visitData} />
                                    </Tab>
                                    <Tab eventKey="medications" title="Home Medications">
                                        <RawDataTable 
                                            data={visitData?.home_medications || []} 
                                            columns={['Name', 'Generic_name', 'Med_class']}
                                        />
                                    </Tab>
                                    <Tab eventKey="medication_orders" title="Med Orders">
                                        <RawDataTable 
                                            data={visitData?.medication_orders || []}
                                            columns={['CSN', 'Med_name']}
                                        />
                                    </Tab>
                                    <Tab eventKey="procedure_orders" title="Proc Orders">
                                        <RawDataTable 
                                            data={visitData?.procedure_orders || []}
                                            columns={['CSN', 'Proc_name']}
                                        />
                                    </Tab>
                                    <Tab eventKey="labs" title="Labs">
                                        <RawDataTable 
                                            data={visitData?.labs || []}
                                            columns={[
                                                'Display_name',
                                                'Component_name',
                                                'Component_result',
                                                'Component_units',
                                                'Component_abnormal',
                                                'Component_nml_low',
                                                'Component_nml_high'
                                            ]}
                                        />
                                    </Tab>
                                    <Tab eventKey="radiology" title="Radiology">
                                        <RawDataTable 
                                            data={visitData?.radiology || []}
                                            columns={['Study', 'Impression_DeID']}
                                        />
                                    </Tab>
                                    <Tab eventKey="pmh" title="PMH">
                                        <RawDataTable 
                                            data={visitData?.pmh || []}
                                            columns={['Noted_date', 'Desc10', 'DescCCS']}
                                        />
                                    </Tab>
                                    <Tab eventKey="vitals" title="Vitals">
                                        <RawDataTable 
                                            data={visitData?.vitals || []}
                                            columns={['Measure', 'max', 'min', 'last']}
                                        />
                                    </Tab>
                                </Tabs>
                            </div>
                        </Col>
                        <Col md={6}>
                            <h5>Patient Documents</h5>
                            <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '600px', overflowY: 'auto' }}>
                                {patientDocuments}
                            </pre>
                        </Col>
                    </Row>
                </div>

                <div ref={edNotesRef}>
                    <Row className="my-4">
                        <Col>
                            <div className="d-flex align-items-center">
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                                <div className="px-3">
                                    <h4 className="mb-0 text-muted">ED Notes</h4>
                                </div>
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                            </div>
                        </Col>
                    </Row>

                    <Row style={{marginBottom: '12px'}}>
                        <Col md={6}>
                            <h5>ED Note</h5>
                            <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '600px', overflowY: 'auto' }}>
                                {edNote}
                            </pre>
                        </Col>
                        <Col md={6}>
                            <h5>Next Visit ED Note</h5>
                            <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '600px', overflowY: 'auto' }}>
                                {nextVisitEdNote}
                            </pre>
                        </Col>
                    </Row>
                </div>

                <div ref={dischargeComparisonRef}>
                    <Row className="my-4">
                        <Col>
                            <div className="d-flex align-items-center">
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                                <div className="px-3">
                                    <h4 className="mb-0 text-muted">Original Discharge Instructions Assessment</h4>
                                </div>
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <h5>Original Discharge Instruction</h5>
                            <Row>
                                <Col md={6}>
                                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        <pre style={{ whiteSpace: 'pre-wrap' }}>
                                            {originalDischargeInstruction}
                                        </pre>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        <SurveyForm<OriginalSurvey>
                                            survey={originalSurvey} 
                                            setSurvey={setOriginalSurvey}
                                            surveyType="original"
                                            onSubmit={handleOriginalSurveySubmit}
                                            isSubmitted={surveyStatuses.original.isSubmitted}
                                            isUpdate={surveyStatuses.original.isUpdate}
                                            submittedAt={surveyStatuses.original.submittedAt}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>

                <div ref={videoReviewRef}>
                    <Row className="my-4">
                        <Col>
                            <div className="d-flex align-items-center">
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                                <div className="px-3">
                                    <h4 className="mb-0 text-muted">Generated Video Review</h4>
                                </div>
                                <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col md={12}>
                            <h5 className="text-center">Generated Discharge Instructions Text (Video Transcript)</h5>
                            <div style={{ 
                                maxHeight: '300px', 
                                overflowY: 'auto',
                                padding: '20px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                margin: '0 20px'
                            }}>
                                <pre style={{ whiteSpace: 'pre-wrap' }}>
                                    {generatedDischargeSummary}
                                </pre>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={7}>
                            <h5 className="text-center">Discharge Instructions Video</h5>
                            <div className="d-flex justify-content-center align-items-center" 
                                 style={{ minHeight: '500px' }}>
                                {videoUrl ? (
                                    <div>
                                        {/* Try the standard video element first */}
                                        <video 
                                            key={videoUrl}
                                            controls 
                                            preload="auto"
                                            style={{ 
                                                width: '100%',
                                                maxWidth: '900px',
                                                height: 'auto',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                            }}
                                            src={videoUrl}
                                        >
                                            <source src={videoUrl} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                        
                                        {/* Add a direct link for when the video can't load in the browser */}
                                        <div className="mt-3 text-center">
                                            <a
                                                href={videoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-primary"
                                            >
                                                Open Video in New Tab
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted">
                                        {csn ? "Loading video..." : "No video available"}
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col md={5}>
                            <h5 className="text-center">Video Assessment</h5>
                            <div style={{ 
                                maxHeight: '700px',
                                overflowY: 'auto',
                                padding: '30px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                margin: '0 20px'
                            }}>
                                <SurveyForm<VideoSurvey>
                                    survey={videoSurvey}
                                    setSurvey={setVideoSurvey}
                                    surveyType="video"
                                    onSubmit={handleVideoSurveySubmit}
                                    isSubmitted={surveyStatuses.video.isSubmitted}
                                    isUpdate={surveyStatuses.video.isUpdate}
                                    submittedAt={surveyStatuses.video.submittedAt}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

const getTimeInMs = (timestamp: any): number => {
    if (timestamp instanceof Date) {
        return timestamp.getTime();
    }
    
    if (typeof timestamp === 'number') {
        return timestamp;
    }
    
    if (typeof timestamp === 'string') {
        // Try to parse as ISO date first
        const parsedDate = new Date(timestamp);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate.getTime();
        }
        
        // Try to parse as numeric string
        const numericValue = parseInt(timestamp, 10);
        if (!isNaN(numericValue)) {
            return numericValue;
        }
    }
    
    console.error("Could not parse timestamp:", timestamp);
    return 0; // Use 0 as error value - which will make banners disappear
} 